import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * Labels, tones and the two coloured atoms for the political register.
 *
 * ## Every lookup falls back to the RAW CODE
 *
 * Each code here is a `varchar(32)` written by a `@Converter` enum whose last
 * constant is `UNKNOWN`/`__UNKNOWN__`. A code this build has never heard of is
 * forward compatibility working, not a bug — so an unrecognised code renders as
 * itself. Falling back to the word "Unknown" would collapse two different
 * unknown codes into one thing on screen, and nobody could tell which record was
 * which.
 *
 * ## Where the code LISTS come from
 *
 * The server. `listSeatTypes()` publishes `{code, requiredUnitLevel}`, and
 * `GET /politics/vocabulary` publishes `partyStatus`, `coalitionStatus`,
 * `candidateStatus` and `partyCoalitionRole` — read through
 * `usePoliticsCodes()` in `features/polling/useVocabulary.ts`. Every picker that
 * writes one of those codes is built from that response, in the order the server
 * sends it: the candidate list arrives in NOMINATION order, which is a fact
 * about the path a candidacy takes and not a sort this file gets to choose.
 *
 * What is left here is the ENGLISH. A code with no label renders as itself, so a
 * seventh status the server adds appears on screen the day it is served, plainly
 * missing a label rather than plainly missing.
 *
 * There are no `*_ORDER` arrays here any more. Every picker and every filter bar
 * reads `usePoliticsCodes()`, so the arrays had no reader — and an unused
 * constant is not harmless, it is a list the next person reaches for.
 *
 * ## Colour is data, never a name→colour table
 *
 * `colourHex` arrives on the party and on the coalition. `Swatch` and
 * `EntityChip` read it and nothing else. PollSphere kept a hardcoded substring
 * table (`Sakaja→#16a34a`) duplicated in two files, so a sixth candidate was grey
 * and a renamed one changed colour.
 */

// ── seat types ──────────────────────────────────────────────────────────────

const SEAT_TYPE_LABEL: Record<string, string> = {
  PRESIDENT: 'President',
  GOVERNOR: 'Governor',
  SENATOR: 'Senator',
  WOMAN_REP: 'Woman Representative',
  MP: 'Member of Parliament',
  MCA: 'Member of County Assembly',
}
export const seatTypeLabel = (c?: string | null) => (!c ? '—' : (SEAT_TYPE_LABEL[c] ?? c))

/**
 * The seat-scope rule in one sentence, from the server's own mapping.
 *
 * Shown beside the unit picker and on the candidate drawer. Both read the same
 * `requiredUnitLevel` the server published, so the sentence cannot drift from the
 * rule the server will enforce — and when the level is missing the sentence says
 * that rather than inventing a level.
 */
export function seatScopeSentence(seatCode: string, requiredUnitLevel?: string | null): string {
  if (!requiredUnitLevel) {
    return `The server publishes no unit level for ${seatTypeLabel(seatCode)} (${seatCode}).`
  }
  return `A ${seatCode} contests a ${requiredUnitLevel}.`
}

// ── party status ────────────────────────────────────────────────────────────


const PARTY_STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Active', SUSPENDED: 'Suspended', DEREGISTERED: 'Deregistered',
}
export const partyStatusLabel = (c?: string | null) => (!c ? '—' : (PARTY_STATUS_LABEL[c] ?? c))

/**
 * ONE accent per screen: status carries colour, and everything that is not a
 * status is ink — except the party/coalition colour itself, which is the record's
 * own identity rather than a judgement about it.
 */
export const partyStatusTone = (c?: string | null): string => {
  switch (c) {
    case 'ACTIVE': return 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30'
    case 'SUSPENDED': return 'bg-amber-500/10 text-amber-600 border-amber-500/30'
    case 'DEREGISTERED': return 'bg-muted text-muted-foreground border-border'
    default: return 'bg-muted/60 text-foreground/70 border-border'
  }
}

// ── coalition status ────────────────────────────────────────────────────────


const COALITION_STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Active', DISSOLVED: 'Dissolved',
}
export const coalitionStatusLabel = (c?: string | null) =>
  (!c ? '—' : (COALITION_STATUS_LABEL[c] ?? c))

export const coalitionStatusTone = (c?: string | null): string => {
  switch (c) {
    case 'ACTIVE': return 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30'
    case 'DISSOLVED': return 'bg-muted text-muted-foreground border-border'
    default: return 'bg-muted/60 text-foreground/70 border-border'
  }
}

// ── candidate status ────────────────────────────────────────────────────────

/**
 * The nomination path, in order. A status pill is a position on it.
 */
const CANDIDATE_STATUS_LABEL: Record<string, string> = {
  PROSPECTIVE: 'Prospective', NOMINATED: 'Nominated', CLEARED: 'Cleared',
  WITHDRAWN: 'Withdrawn', DISQUALIFIED: 'Disqualified', ELECTED: 'Elected',
}
export const candidateStatusLabel = (c?: string | null) =>
  (!c ? '—' : (CANDIDATE_STATUS_LABEL[c] ?? c))

export const candidateStatusTone = (c?: string | null): string => {
  switch (c) {
    case 'ELECTED': return 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30'
    case 'CLEARED': return 'bg-sky-500/10 text-sky-600 border-sky-500/30'
    case 'NOMINATED': return 'bg-indigo-500/10 text-indigo-600 border-indigo-500/30'
    case 'WITHDRAWN': return 'bg-muted text-muted-foreground border-border'
    case 'DISQUALIFIED': return 'bg-rose-500/10 text-rose-600 border-rose-500/30'
    case 'PROSPECTIVE': return 'bg-muted/60 text-foreground/70 border-border'
    default: return 'bg-muted/60 text-foreground/70 border-border'
  }
}

// ── coalition role ──────────────────────────────────────────────────────────

const ROLE_LABEL: Record<string, string> = {
  LEAD: 'Lead', MEMBER: 'Member', OBSERVER: 'Observer',
}
export const roleLabel = (c?: string | null) => (!c ? '—' : (ROLE_LABEL[c] ?? c))

// ── numbers ─────────────────────────────────────────────────────────────────

/**
 * A count, or an em dash when there is none to show.
 *
 * `?? 0` is banned across this feature: a zero is a claim that the register holds
 * nothing, and "the server did not send this" is a different statement.
 */
export const count = (n: number | null | undefined) =>
  (n === null || n === undefined ? '—' : n.toLocaleString())

// ── the two coloured atoms ──────────────────────────────────────────────────

const HEX = /^#[0-9a-fA-F]{6}$/

/**
 * The colour a record carries, as a square.
 *
 * Rendered from `colourHex` alone. A null or malformed value gets a hollow
 * outline rather than a default colour — a party with no declared colour must not
 * be indistinguishable from a party that declared grey.
 */
export function Swatch({ colourHex, className, title }: {
  colourHex?: string | null
  className?: string
  title?: string
}) {
  const ok = !!colourHex && HEX.test(colourHex)
  return (
    <span
      aria-hidden
      title={title ?? (ok ? colourHex! : 'No colour declared')}
      className={cn(
        'inline-block h-3 w-3 rounded-[3px] border shrink-0',
        ok ? 'border-black/10' : 'border-dashed border-muted-foreground/50',
        className,
      )}
      style={ok ? { backgroundColor: colourHex! } : undefined}
    />
  )
}

export interface EntityLike {
  code: string
  name: string
  shortName?: string | null
  colourHex?: string | null
}

/**
 * A party or a coalition, rendered in its own colour.
 *
 * The tint and the border come from `colourHex` at low alpha; the TEXT stays ink.
 * A party's own colour as label text is unreadable about a third of the time —
 * `#facc15` on white is invisible — and the identity is already carried by the
 * swatch and the tint, which cannot fail that way. With no colour the chip falls
 * back to plain ink on the muted surface, which is what "this record declared no
 * colour" should look like.
 */
export function EntityChip({ entity, sub, className }: {
  entity: EntityLike | null
  /** A trailing note in the chip — "derived", a role, a join date. */
  sub?: ReactNode
  className?: string
}) {
  if (!entity) return <span className="text-erp-xs text-muted-foreground">—</span>
  const ok = !!entity.colourHex && HEX.test(entity.colourHex)
  return (
    <span
      title={entity.name}
      className={cn(
        'inline-flex items-center gap-1.5 rounded border px-1.5 py-0.5 text-erp-xs max-w-full',
        !ok && 'bg-muted/60 border-border text-foreground/80',
        className,
      )}
      style={ok
        // 8-digit hex: the record's colour at 14% for the fill and 45% for the
        // edge, so the chip reads as that colour in both themes without ever
        // putting the raw hue behind the text.
        ? { backgroundColor: `${entity.colourHex}24`, borderColor: `${entity.colourHex}73` }
        : undefined}
    >
      <Swatch colourHex={entity.colourHex} className="h-2.5 w-2.5" />
      <span className="truncate">{entity.shortName || entity.name}</span>
      {sub && <span className="text-muted-foreground shrink-0">{sub}</span>}
    </span>
  )
}

/** One pill shape for every status in this feature. */
export function Pill({ tone, children, className }: {
  tone?: string
  children: ReactNode
  className?: string
}) {
  return (
    <span className={cn(
      'inline-flex items-center rounded border px-1.5 py-0.5 text-erp-xs whitespace-nowrap',
      tone ?? 'bg-muted/60 text-foreground/70 border-border',
      className,
    )}>
      {children}
    </span>
  )
}
